// パーツ一覧（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { SERVICE, SIZE } from './config.js';
import { txt } from './text.js';
import { HANDS, handName, isPair, pairMate, pairThumbSide, slotKeys, thumbMate } from './merge.js';
import { anim } from './store.js';
import { $, el, keyOf, reveal } from './util.js';
import { findPart, hasLayers, layersOf, partIsEmpty, partsList } from './core/data.js';
import { C, catByKey } from './core/model.js';
import { readStored } from './core/savedata.js';
import { loadSave as applySave } from './loadsave.js';
import { buildLayerPick } from './categories.js';
import { buildColors } from './lr.js';
import { updateTools } from './tools.js';
import { histCut, snapshot, toast } from './history.js';
import type { Category, Part, SaveData } from './types.js';
import { setPart } from './core/ops.js';
import { EXCLUSIVE, clearExclusive } from './core/exclusive.js';
import { gridHint, liveNone, liveThumb, liveThumbOf, thumbClean } from './lookrules.js';
import { CONFIG } from './config.js';
import { on } from './core/events.js';

// パーツの一覧のボタン（カテゴリとパーツ、サムネイルを作ったか）と一覧（カテゴリのキーと数）
export interface PartButton extends HTMLButtonElement { _cat: Category; _part: Part; _thumb?: boolean }
export interface PartGrid extends HTMLDivElement { _key: string; _count: number }
// パーツの範囲（getBBox で測ったもの）
export interface ThumbBox { x: number; y: number; width: number; height: number }
// サムネイルの寄せ方: 本物はカテゴリごとの決め打ち（SCALE = [横の寄せ, 縦の寄せ, 倍率]）で切り取るので、小さいパーツは豆粒に、
// ずれたパーツははみ出していた（2026-10-11 に数えたもの: 持ちもの 190 のうち 121 がマスの 35% 未満・26 がはみ出し、くびもと 99 のうち 68、ぼうし 311 のうち 77）。
// モックはパーツごとに範囲（getBBox）を測ってまん中に置き、大きさは「カテゴリの倍率で見たときの大きさ」を THUMB_MIN〜THUMB_MAX
// （マスの辺に対する割合）に収める。その間ならカテゴリの中での大きさの違いはそのまま、小さいものは見えるまで大きく、はみ出すものは全体が入るまで小さく。
// 範囲は一覧に入れたサムネイルの g で測る（いくつかまとめて、入れる → 範囲を読む → 寄せる の順。読む・書くを交互にするとレイアウトが毎回走る）。
// 測った範囲はパーツごとに覚える（同じパーツのカテゴリ、ぼうし2 などでも使う）。プリセット（キャラ）は全身の絵なので今のまま。
// 左右でひと組（DRESSUP2 のめ など）は、同じ番号のもう一方の絵も入れて両方を1枚に（2026-10-27。左だけ・右だけの一覧も同じ）
export const THUMB_MIN = 0.6, THUMB_MAX = 0.9;
export const thumbBoxes: Record<string, ThumbBox> = {}; // NAME:パーツ番号 → 範囲
export function thumbnail(cat: Category, part: Part) {
  const svg = el<SVGSVGElement>('svg', { viewBox: '0 0 ' + SIZE + ' ' + SIZE, 'aria-hidden': 'true' });
  // アイシャドウ・インナーカラー・スカートの裏地（DRESSUP2）: いまのキャラから作る絵（lookrules.ts）
  // 「なし」は空のタイル（makeGrid が「なし」と書く。addThumbs は g を測る）。いまのキャラから作る絵のカテゴリでは、中身の無いパーツ（まゆ の 0000 など）も同じ（土台だけの絵に「なし」と重ならないように）
  if (liveNone(cat, part) || (liveThumb(cat) && partIsEmpty(cat, part))) { svg.appendChild(el<SVGGElement>('g')); return svg; }
  const live = liveThumb(cat) ? liveThumbOf(cat, part) : null;
  if (live) { hideInvisible(live); svg.appendChild(live); return svg; }
  const g = el<SVGGElement>('g');
  // 写しにはどのレイヤーかを書いておく（data-layer。はみ出しを隠す肌色の形をレイヤーで見分ける lookrules.ts の thumbSkinBgLayers）
  const put = (c: Category, src: Element | null, i: number) => { if (!src) return; const n = src.cloneNode(true) as Element; if (c.LAYER[i]) n.setAttribute('data-layer', c.LAYER[i]); g.appendChild(n); };
  layersOf(cat, part).forEach((src, i) => { put(cat, src, i); });
  const mate = thumbMate(keyOf(cat)), mc = mate ? catByKey[mate] : null, mp = mc ? findPart(mc, part.id) : null; // 「左」「右」のときは片方だけ（設定 pairThumbSide）
  if (mc && mp) layersOf(mc, mp).forEach((src, i) => { put(mc, src, i); });
  thumbClean(cat, g); // アイシャドウの形はアイシャドウのカテゴリで（め のサムネイルには出さない）・はみ出しを隠す肌色の形を外す（lookrules.ts）
  hideInvisible(g);
  svg.appendChild(g);
  return svg;
}
// 見えない形（不透明度 0。パーツに入っている、ぼかしの範囲などのための小さな丸）はサムネイルの範囲に入れない（隠しても見た目は同じ）。
// KAWAII のハイライトは目より上の離れた所にあり、アイシャドウ・ハイライトの絵が下に寄っていた（ユーザー 2026-10-29）
const ZERO_OPACITY = /(^|;)\s*opacity\s*:\s*0(\.0*)?\s*(;|$)/;
function hideInvisible(g: Element) {
  g.querySelectorAll('[style*="opacity"], [opacity]').forEach((n) => {
    const op = n.getAttribute('opacity');
    if (ZERO_OPACITY.test(n.getAttribute('style') || '') || (op != null && op.trim() !== '' && Number(op) === 0)) (n as SVGElement).style.display = 'none';
  });
}
const SHAPE_SEL = 'path,rect,circle,ellipse,polygon,polyline,line,use,image,text';
function nearBox(g: SVGGElement, near: number, side: string | null): ThumbBox | null {
  const svg = g.ownerSVGElement, ctm = svg && svg.getScreenCTM();
  if (!svg || !ctm) return null;
  const inv = ctm.inverse();
  const rects: ThumbBox[] = [];
  g.querySelectorAll(SHAPE_SEL).forEach((n) => {
    if (n.closest('[data-nobox]') || n.closest('defs,clipPath,mask,pattern')) return;
    const r = n.getBoundingClientRect();
    if (!r.width && !r.height) return;
    const p1 = new DOMPoint(r.left, r.top).matrixTransform(inv), p2 = new DOMPoint(r.right, r.bottom).matrixTransform(inv);
    rects.push({ x: Math.min(p1.x, p2.x), y: Math.min(p1.y, p2.y), width: Math.abs(p2.x - p1.x), height: Math.abs(p2.y - p1.y) });
  });
  let use = rects;
  if (side) {
    const half = rects.filter((r) => (side === 'left' ? r.x + r.width / 2 < SIZE / 2 : r.x + r.width / 2 >= SIZE / 2));
    if (half.length) use = half;
  }
  if (!use.length) return null;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  use.forEach((r) => { x1 = Math.min(x1, r.x); y1 = Math.min(y1, r.y); x2 = Math.max(x2, r.x + r.width); y2 = Math.max(y2, r.y + r.height); });
  const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2, w = Math.max(x2 - x1, near), h = Math.max(y2 - y1, near);
  return { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
}
export function thumbBox(cat: Category, part: Part, g: SVGGElement): ThumbBox | null {
  if (cat.NAME === 'chara') return null;
  const live = liveThumb(cat); // いまのキャラから作るサムネイルは範囲を覚えない
  const key = cat.NAME + ':' + part.id + (thumbMate(keyOf(cat)) ? ':pair' : '');
  if (!live && thumbBoxes[key]) return thumbBoxes[key];
  let b: { x: number; y: number; width: number; height: number } | null = null;
  // パーツのまわりに寄せる（data-near・data-side。lookrules.ts の onBase の near・side。ほくろ・イヤリングなど小さいもの）: 土台を除いたパーツの形の範囲を、
  // 画面の上の四角から SVG の座標に戻して合わせる（side は画面の右・左の半分にあるものだけ＝両耳のイヤリングは片耳に寄せる）。near はその四角より小さければ near 四方に広げる
  const near = live ? Number(g.getAttribute('data-near')) || 0 : 0, side = live ? g.getAttribute('data-side') : null;
  if (near || side) b = nearBox(g, near, side);
  // 決まった切り取り（data-box="x,y,w,h"。lookrules.ts の onBase の box）
  const fixed = live ? (g.getAttribute('data-box') || '').split(',').map(Number) : [];
  if (fixed.length === 4 && fixed.every((v) => !isNaN(v)) && fixed[2] > 0 && fixed[3] > 0) b = { x: fixed[0], y: fixed[1], width: fixed[2], height: fixed[3] };
  // 範囲に入れない所（data-nobox。ハイライトの一覧の、め に重ねたハイライト）は、隠して計ってから戻す
  const skip = live && !b ? Array.prototype.slice.call(g.querySelectorAll('[data-nobox]')) as SVGElement[] : [];
  skip.forEach((n) => { n.style.display = 'none'; });
  if (!b) { try { b = g.getBBox(); } catch (e) {} }
  skip.forEach((n) => { n.style.removeProperty('display'); });
  if (!b || !b.width || !b.height) return null; // 空のパーツ・まだ描かれていないとき（覚えない）
  const box = { x: b.x, y: b.y, width: b.width, height: b.height };
  // まわりの余白（data-pad。1 辺の長いほうに対する割合。lookrules.ts の onBase で片方の目だけのとき）
  const pad = live ? Number(g.getAttribute('data-pad')) || 0 : 0;
  if (pad > 0) { const d = Math.max(box.width, box.height) * pad; box.x -= d; box.y -= d; box.width += d * 2; box.height += d * 2; }
  return live ? box : (thumbBoxes[key] = box);
}
export function thumbTransform(cat: Category, box: ThumbBox | null | undefined) {
  const ax = cat.SCALE[0], ay = cat.SCALE[1], s = cat.SCALE[2];
  if (box) {
    const side = Math.max(box.width, box.height);
    // いまのキャラから作るアイシャドウの絵は、め の一覧と同じくらいに大きく（カテゴリの倍率は番号の絵のためのもの）
    const size = liveThumb(cat) ? THUMB_MAX : Math.max(THUMB_MIN, Math.min(THUMB_MAX, side * s / SIZE));
    const k = size * SIZE / side, cx = box.x + box.width / 2, cy = box.y + box.height / 2;
    return 'translate(' + SIZE / 2 + ',' + SIZE / 2 + ') scale(' + (cat.REVERSE == 1 ? -k : k) + ',' + k + ') translate(' + -cx + ',' + -cy + ')';
  }
  const xm = (SIZE * s - SIZE) * ax, ym = (SIZE * s - SIZE) * ay;
  return cat.REVERSE == 1
    ? 'translate(' + (SIZE + xm) + ',' + -ym + ') scale(' + -s + ',' + s + ')'
    : 'translate(' + -xm + ',' + -ym + ') scale(' + s + ')';
}
// サムネイルをまとめて付ける
export function addThumbs(btns: PartButton[]) {
  const made: { btn: PartButton; g: SVGGElement; box?: ThumbBox | null }[] = [];
  btns.forEach((btn) => {
    if (btn._thumb) return;
    btn._thumb = true;
    const svg = thumbnail(btn._cat, btn._part);
    btn.insertBefore(svg, btn.firstChild);
    if (S.observer) S.observer.unobserve(btn);
    made.push({ btn: btn, g: svg.firstChild as SVGGElement });
  });
  made.forEach((m) => { m.box = thumbBox(m.btn._cat, m.btn._part, m.g); });
  made.forEach((m) => { m.g.setAttribute('transform', thumbTransform(m.btn._cat, m.box)); });
}
export function presetName(id: number) {
  const row = C.data.CHARA.filter((r) => Number(r[0]) === id)[0];
  if (!row) return '';
  if (id === 1) return txt('slot_last');
  // データの名前は日本語の「デフォキャラ A」（CHOCO）・「デフォA」（DRESSUP）など。ほかの言語では辞書の preset_name で（A などはそのまま）。
  // DRESSUP の「初期化」（はじめのキャラ）は preset_initial
  if (row[1] === '初期化') return txt('preset_initial');
  const m = /^デフォ(?:キャラ)?\s*(.+)$/.exec(row[1]);
  return m ? txt('preset_name', { x: m[1] }) : row[1];
}
// 一覧の印: 選んでいるパーツ（selId）と、重ね付けのもう一方の置き場所に入っているパーツ（その置き場所の印 1・2・左・右）
export function markParts(cat: Category, grid: PartGrid, selId: number | null | undefined) {
  const key = keyOf(cat);
  // 左右の一覧の絵を側ごとに描くメーカー（設定 pairThumbSide）の「左」「右」では、反対側の印（右・左）は出さない（一覧は全部その側の絵なので、反対側の印は紛らわしい。ユーザー 2026-10-29）
  const pair = slotKeys(key), otherKey = pair && !pairThumbSide(key) ? pair[key === pair[0] ? 1 : 0] : null;
  const otherId = otherKey && C.state[otherKey] ? C.state[otherKey].parts : null;
  const otherMark = otherKey ? txt(HANDS[pair![0]] ? handName(pair![0], otherKey === pair![1] ? 1 : 0, true) : isPair(key) ? (otherKey === pair![1] ? 'hand_r_mark' : 'hand_l_mark') : (otherKey === pair![1] ? 'layer_2_mark' : 'layer_1_mark')) : '';
  Array.prototype.forEach.call(grid.children, (b: PartButton) => {
    b.classList.toggle('sel', b._part.id === selId);
    const other = otherId != null && b._part.id === otherId && b._part.id !== partsList(cat.NAME)[0].id;
    b.classList.toggle('other', other);
    if (other) b.dataset.mark = otherMark; else delete b.dataset.mark;
  });
}
// いまのキャラから作るサムネイル（アイシャドウ）を作り直す。shown: 見えているものだけすぐ（ほかは見えたとき）
function resetThumbs(grid: PartGrid) {
  Array.prototype.forEach.call(grid.children, (b: PartButton) => {
    if (!b._thumb) return;
    const svg = b.querySelector('svg');
    if (svg) svg.remove();
    b._thumb = false;
  });
}
// 開いているのがアイシャドウなら、キャラが変わるたび（め・ハイライト・アイシャドウの色など）に作り直す。まとめて次のフレームで 1 回
let liveRaf = 0;
on('drawn', () => {
  if (liveRaf || !liveThumb(catByKey[S.selKey]) || !grids[S.selKey]) return;
  liveRaf = requestAnimationFrame(() => {
    liveRaf = 0;
    const g = grids[S.selKey];
    if (!g || !liveThumb(catByKey[S.selKey])) return;
    resetThumbs(g.el);
    addThumbs(Array.prototype.slice.call(g.el.children));
  });
});
// 一覧はカテゴリごとに作って取っておき、開き直したときは差し替えるだけにする（最近の GRID_KEEP 個まで）。
// 見えているサムネイルは一覧を出すのと同じフレームで作る（作り直して空のタイルが1フレーム見えていたのが「カクッ」の元）。
// 残りは見えたときに作り、たくさん来たら 1 フレーム 8ms までに分ける
export const GRID_KEEP = 8;
export const grids: Record<string, { el: PartGrid; scroll: number | null; scrollX?: number; sel: number | null | undefined }> = {};           // カテゴリのキー → { el, scroll（前のスクロール位置）, sel（そのときの選んでいたパーツ） }
export const thumbQueue: PartButton[] = [];
export function drainThumbs() {
  S.thumbRaf = 0;
  const t0 = performance.now();
  while (thumbQueue.length && performance.now() - t0 < 8) {
    // 差し替えで外れた一覧のものは、また見えたときに作る
    var t1 = performance.now();
    addThumbs(thumbQueue.splice(0, S.thumbBatch).filter((b) => b.isConnected));
    if (performance.now() - t1 > 24) S.thumbBatch = 1;
  }
  if (thumbQueue.length) S.thumbRaf = requestAnimationFrame(drainThumbs);
}
export function makeGrid(cat: Category) {
  const list = partsList(cat.NAME).filter(hasLayers);
  const isChara = cat.NAME === 'chara';
  const grid = document.createElement('div') as PartGrid;
  grid.className = 'grid';
  grid._key = keyOf(cat);
  // サムネイルの細い縁（設定 thumbOutline。め・くち・メイクなどは付けない＝skip。2026-10-28）
  const outline = CONFIG.thumbOutline;
  if (outline && (outline.skip || []).indexOf(keyOf(cat)) < 0) grid.classList.add('thumb-outline');
  grid._count = list.length;
  // 色だけを選ぶカテゴリ（DRESSUP2 の はだ。パーツは1つだけ）: 一覧の代わりにひとこと（パーツのボタンは CSS で隠す。
  // 一覧の子はパーツのボタンだけにしておく＝ひとことは CSS の ::before で出す）
  const only = CONFIG.colorOnly && CONFIG.colorOnly[keyOf(cat)];
  if (only) {
    grid.classList.add('color-only');
    grid.dataset.note = txt(only);
  }
  list.forEach((part) => {
    const btn = document.createElement('button') as PartButton;
    btn.type = 'button';
    btn.className = 'part';
    btn._cat = cat;
    btn._part = part;
    const label = document.createElement('span');
    if (isChara) {
      label.className = 'label';
      label.textContent = presetName(part.id);
    } else {
      label.className = 'no';
      label.textContent = ('0000' + part.id).slice(-4);
      // 中身の無いパーツ（たいてい先頭。押すとそのカテゴリのパーツを外す）は「なし」と書く
      if (partIsEmpty(cat, part) || liveNone(cat, part)) {
        btn.classList.add('none');
        const none = document.createElement('span');
        none.className = 'none-label';
        none.textContent = txt('part_none');
        btn.appendChild(none);
      }
    }
    btn.appendChild(label);
    btn.addEventListener('click', () => { choosePart(cat, part, list); });
    grid.appendChild(btn);
  });
  return grid;
}
// 一覧の上のひとこと（lookrules.ts の gridHint。左の手を上げていないときのブレスレットなど）。一覧はそのまま押せる
function setHint(cat: Category, grid: PartGrid) {
  const hint = gridHint(cat);
  grid.classList.toggle('has-hint', !!hint);
  if (hint) grid.dataset.hint = txt(hint); else delete grid.dataset.hint;
}
// 手などを替えたら、開いている一覧のひとことを付け直す
on('drawn', () => { const g = grids[S.selKey], c = catByKey[S.selKey]; if (g && c) setHint(c, g.el); });
export function dropGrid(key: string) {
  const g = grids[key];
  if (!g) return;
  if (S.observer) Array.prototype.forEach.call(g.el.children, (b: Element) => { S.observer!.unobserve(b); });
  delete grids[key];
}
export function buildParts(cat: Category, dir: number) {
  const box = $('parts');
  if (!S.observer) {
    S.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || (entry.target as PartButton)._thumb) return;
        thumbQueue.push(entry.target as PartButton);
        if (!S.thumbRaf) S.thumbRaf = requestAnimationFrame(drainThumbs);
      });
    }, { root: box, rootMargin: '200px' });
  }
  const key = keyOf(cat), st = C.state[key], isChara = cat.NAME === 'chara';
  // いまの一覧のスクロール位置を覚えておく
  const cur = box.firstElementChild as PartGrid | null;
  if (cur && grids[cur._key]) { grids[cur._key].scroll = box.scrollTop; grids[cur._key].scrollX = box.scrollLeft; } // 全身のスマホは左右（CSS）
  const g = grids[key] || (grids[key] = { el: makeGrid(cat), scroll: null, sel: undefined });
  if (liveThumb(cat)) resetThumbs(g.el); // アイシャドウ・インナーカラー・スカートの裏地は開くたびに、いまのキャラで
  S.gridOrder = S.gridOrder.filter((k) => k !== key);
  S.gridOrder.push(key);
  while (S.gridOrder.length > GRID_KEEP) dropGrid(S.gridOrder.shift()!);
  $('catCount').textContent = CONFIG.colorOnly && CONFIG.colorOnly[key] ? '' : txt(isChara ? 'count_presets' : 'count_parts', { n: g.el._count });
  setHint(cat, g.el);
  // 選んでいるパーツの印（ランダムや元に戻すで変わっていることがある）
  const selId = !isChara && st ? st.parts : null;
  markParts(cat, g.el, selId);
  // 入ってくる動き（下で enter を付ける）の準備は、一覧を入れる前に。後で --dir を変えて offsetWidth を読むと、
  // 一覧の全部のタイルのスタイル計算がもう1回走っていた（カテゴリを開くたびに CPU 4 倍遅い端末で 15ms ほど。2026-10-18）。
  // 間で位置を読む（スタイルが確定する）ので、enter を付け直せば動きは毎回はじめから
  g.el.classList.remove('enter');
  if (anim && dir) g.el.style.setProperty('--dir', String(dir));
  if (cur !== g.el) { box.textContent = ''; box.appendChild(g.el); }
  // 前に開いたときの位置に戻す。選んでいるパーツが変わっていたら、それがまん中に来るように
  if (g.scroll != null && g.sel === selId) { box.scrollTop = g.scroll; box.scrollLeft = g.scrollX || 0; }
  else { box.scrollTop = 0; box.scrollLeft = 0; reveal(box, g.el.querySelector('.part.sel'), true); }
  g.sel = selId;
  // 見えているところ（と少し先）のサムネイルは、いま作る。位置を全部読んでから作る（読む・書くを交互にするとレイアウトが毎回走る）
  const r = box.getBoundingClientRect(), top = r.top - 120, bottom = r.bottom + 120, left = r.left - 120, right = r.right + 120, visible: PartButton[] = [];
  Array.prototype.filter.call(g.el.children, (b) => {
    if (b._thumb) return false;
    const br = b.getBoundingClientRect();
    return br.bottom > top && br.top < bottom && br.right > left && br.left < right; // 左右に並ぶ（全身のスマホ）ときも見えている所だけ
  }).forEach((b) => { visible.push(b); });
  addThumbs(visible);
  Array.prototype.forEach.call(g.el.children, (b: PartButton) => { if (!b._thumb) S.observer!.observe(b); });
  // 入ってくる動き（カテゴリの並びの向きから）
  if (anim && dir) g.el.classList.add('enter');
}
export function choosePart(cat: Category, part: Part, list: Part[]) {
  histCut();
  const key = keyOf(cat);
  if (cat.NAME === 'chara') {
    // プリセット: 001 は最後に完成したキャラ（無ければ同じ番号のプリセット）
    const usePreset = (save: SaveData | null) => {
      if (!save) {
        const row = C.data.CHARA.filter((r) => Number(r[0]) === part.id)[0];
        if (row && row[3]) save = JSON.parse(row[3]);
      }
      if (!save) return;
      // 確認は出さずに読み込み、「元に戻す」で作りかけに戻せるようにする
      histCut();
      const snap = snapshot();
      applySave(save);
      updateTools();
      toast(txt('preset_applied', { name: presetName(part.id) }), snap);
    };
    if (part.id === 1) readStored(SERVICE + '_save').then(usePreset); else usePreset(null);
    return;
  }
  // パーツを付ける（もう一度押すと先頭（なし）に戻る・はじめての持ちもの（右）は反転した向き。core/ops.ts）
  const { st, fresh } = setPart(cat, part, list);
  // 付けると外れるカテゴリ（KAWAII の手。core/exclusive.ts）: 外したカテゴリの一覧の印も合わせる
  clearExclusive(key);
  (EXCLUSIVE[key] || []).forEach((k) => { const g = grids[k], c = catByKey[k]; if (g && c && C.state[k]) { markParts(c, g.el, C.state[k].parts); g.sel = C.state[k].parts; } });
  if (slotKeys(key)) buildLayerPick(key);
  // はじめてパーツを付けたカテゴリは、色の欄を作り直して「色・柄」の切りかえを出す（切りかえはパーツの状態があるときだけ出る。
  // 2026-10-22 まで、パーツの無かったカテゴリで付けても、カテゴリを選び直すまで柄を選べなかった）
  if (fresh && S.selKey === key) buildColors(cat);
  // 選んだ印と、もう一方の置き場所の印（左右でひと組の「両方」は右も替わるので、前の右の印を外す）
  if (grids[key]) { markParts(cat, grids[key].el, st.parts); grids[key].sel = st.parts; }
  else document.querySelectorAll<PartButton>('.part').forEach((b) => { b.classList.toggle('sel', b._part.id === st.parts); });
  updateTools();
}
// キャラを軽く弾ませる（ランダム・ロード・入れかえ・元に戻すのとき。パーツやプリセットを選んだときは動かさない＝ユーザー判断 2026-10-11）
export function pop() {
  const stage = $('stage');
  stage.classList.remove('pop');
  void stage.offsetWidth;
  stage.classList.add('pop');
}
